import React from "react";
import _ from "lodash";
import styled from "styled-components";
import classnames from "classnames";
import { ReactComponent as House } from "./../../atoms/icons/house.svg";
import { ReactComponent as Meal } from "./../../atoms/icons/meal.svg";

const Wrap = styled.div`
  flex: 100%;
  padding: 20px 0;
  border-radius: 3px;

  &.disabled {
    opacity: 0.3;
  }

  @media (min-width: 768px) {
    display: flex;
    flex: 1;
    padding: 0;
    max-width: 60%;
  }
`;

const Item = styled.div`
  position: relative;
  padding-top: 20px;
  padding-bottom: 20px;
  padding-left: 40px;
  border-bottom: solid 1px #e6e6e6;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    border-bottom: none;
  }

  @media (min-width: 768px) {
    & {
      width: 50%;
      padding-top: 0;
      padding-right: 15px;
      padding-bottom: 0;
      padding-left: 70px;
      border-bottom: none;
      border-right: solid 1px #e6e6e6;

      &:first-child {
        padding-left: 40px;
      }

      &:last-child {
        border-right: none;
      }

      &:nth-child(4) {
        width: 30%;
        border-right: none;
      }
    }
  }

  @media (min-width: 1024px) {
    & {
      width: 31%;
    }
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: var(--color-primary);
    opacity: 0.1;
  }

  svg {
    position: absolute;
    top: 8px;
    left: 8px;
  }

  &:not(:first-child) {
    &::before {
      top: 15px;
    }

    svg {
      top: 23px;
    }
  }

  @media (min-width: 768px) {
    &::before {
      top: -4px;
    }

    svg {
      top: 4px;
    }

    &:not(:first-child) {
      &::before {
        top: -4px;
        left: 28px;
      }

      svg {
        top: 4px;
        left: 36px;
      }
    }
  }
`;

const ItemTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 16px;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.5;
  letter-spacing: -0.3px;
`;

const ItemContent = styled.div`
  font-size: 14px;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.14;
  letter-spacing: normal;

  p {
    margin-bottom: 8px;
  }

  & *:last-child {
    margin-bottom: 0;
  }
`;

const ItemContentButton = styled.button`
  padding: 0;
  background-color: transparent;
  border: none;
  font-size: 14px;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.14;
  letter-spacing: normal;
  color: var(--color-primary);
  user-select: none;
  outline: none;
  box-shadow: none;

  &:focus,
  &:hover,
  &:active {
    outline: none;
    box-shadow: none;
  }
`;

function DeliveryMealSettings(props) {
  const { shippingAddress, meals, isSkipped, id, exclusions, excludeCodes, isOrder, main_title } = props;

  const redirectToAddesses = () => {
    window.location.href = "/account/addresses/";
  };

  const handleChangeQuantity = (mealId) => {
    props.handleChangeQuantity(mealId, main_title);
  };

  const deliveryExclusions = (exclusionsList, excludeCodesList) => {
    let exclusionData = [];

    if (!excludeCodesList) return "";

    let excludeCodesArray = excludeCodesList.split(",");

    const FLExclusions =
      main_title === "Low-FODMAP" ||
      main_title === "Gluten-Free" ||
      main_title === "Carb-Conscious" ||
      main_title === "B2B" ||
      main_title === "GLP-1";
    const isCarePlan = main_title === "Care";
    let exclusionSkuPrefix;

    if (FLExclusions && !isCarePlan) {
      exclusionSkuPrefix = "FL-";
    } else if (isCarePlan) {
      exclusionSkuPrefix = "CM-";
    } else {
      exclusionSkuPrefix = "MD-";
    }

    exclusionsList.forEach((exclude) => {
      excludeCodesArray.forEach((code) => {
        let flCode = exclusionSkuPrefix + code;
        if (flCode === exclude.sku) exclusionData.push(exclude.name);
      });
    });

    exclusionData = [...new Set(exclusionData)];

    return _.join(exclusionData, ", ");
  };

  const filterMealsAccordingToType = (mealsList, type) => {
    let list = [];

    mealsList.forEach((meal) => {
      meal.tags.forEach((tag) => {
        if (tag.toLowerCase() === type.toLowerCase()) {
          list.push(meal);
        }
      });
    });

    return list;
  };

  const getMealsQuantity = (data) => {
    let quantity = 0;

    data.forEach((meal) => {
      quantity += meal.quantity;
    });

    return quantity;
  };

  let breakfasts = filterMealsAccordingToType(meals, "Breakfast");
  let entreeMeals = filterMealsAccordingToType(meals, "Entrée");
  let entrees = entreeMeals;
  let breakfastsQuantity = getMealsQuantity(breakfasts);
  let entreesQuantity = getMealsQuantity(entrees);

  let exclusionString = deliveryExclusions(exclusions, excludeCodes);
  let breakfastsMarkup = breakfastsQuantity > 0 ? breakfastsQuantity + " Breakfasts," : null;
  let entreesMarkup = entreesQuantity > 0 ? entreesQuantity + " Entrées" : null;

  return (
    <>
      <Wrap className={classnames({ "disabled delivery-item__ct": isOrder }, { "delivery-item__ct": !isOrder })}>
        <Item>
          <House />
          <ItemTitle>Delivery Address</ItemTitle>
          <ItemContent>
            <p>{shippingAddress}</p>
            <p style={{ display: 'none' }}>
              <ItemContentButton disabled={isSkipped || isOrder} onClick={redirectToAddesses}>
                Change
              </ItemContentButton>
            </p>
          </ItemContent>
        </Item>
        <Item>
          <Meal />
          <ItemTitle>Meal Quantities</ItemTitle>
          <ItemContent>
            <p>{props.main_title}</p>
            <p>
              {breakfastsMarkup} {entreesMarkup}
              <br />
              {exclusionString.length > 0 && "Exclude: " + exclusionString}
              {exclusionString.length === 0 && "Exclude: None"}
            </p>
            <p>
              <ItemContentButton
                disabled={isSkipped || isOrder}
                onClick={() => {
                  handleChangeQuantity(id);
                }}
              >
                Change
              </ItemContentButton>
            </p>
          </ItemContent>
        </Item>
      </Wrap>
    </>
  );
}

export default DeliveryMealSettings;
 